import { useEffect, useRef } from 'react'
import { CONFETTI_LANDING_SOUND_START, CONFETTI_END } from './winCelebrationTiming.js'

// Comemoração sintetizada: lançamento dos confetes e batidas de sino de venda.
export default function useWinBell() {
  const active = useRef(null)
  const stop = () => {
    const previous = active.current
    active.current = null
    if (!previous) return
    clearTimeout(previous.timer)
    if (previous.context && previous.context.state !== 'closed') previous.context.close().catch(() => {})
  }
  useEffect(() => stop, [])
  return async () => {
    stop()
    const AudioContext = window.AudioContext || window.webkitAudioContext
    if (!AudioContext) return
    let playing
    try {
      const context = new AudioContext()
      playing = { context, timer: null }
      active.current = playing
      if (context.state === 'suspended') await context.resume()
      if (active.current !== playing || context.state !== 'running') return
      const output = context.createGain()
      output.gain.value = 0.32
      const compressor = context.createDynamicsCompressor()
      compressor.threshold.value = -16
      compressor.knee.value = 12
      compressor.ratio.value = 5
      output.connect(compressor)
      compressor.connect(context.destination)
      const start = context.currentTime + 0.015
      // Ruído filtrado: estalo leve seguido do sopro dos lançadores.
      const noise = context.createBuffer(1, Math.ceil(context.sampleRate * .65), context.sampleRate)
      const samples = noise.getChannelData(0)
      for (let i = 0; i < samples.length; i++) samples[i] = Math.random() * 2 - 1
      for (const offset of [0]) {
        const source = context.createBufferSource()
        const filter = context.createBiquadFilter()
        const envelope = context.createGain()
        source.buffer = noise
        filter.type = 'bandpass'
        filter.Q.value = .6
        filter.frequency.setValueAtTime(2600, start + offset)
        filter.frequency.exponentialRampToValueAtTime(650, start + offset + .6)
        envelope.gain.setValueAtTime(0, start + offset)
        envelope.gain.linearRampToValueAtTime(.5, start + offset + .008)
        envelope.gain.exponentialRampToValueAtTime(.0001, start + offset + .6)
        source.connect(filter); filter.connect(envelope); envelope.connect(output)
        source.start(start + offset); source.stop(start + offset + .65)
      }
      // Eco discreto só nos sinos, com repetições progressivamente mais suaves.
      const bellBus = context.createGain()
      const bellStart = start + .18
      const bellEnd = bellStart + 2.5
      // Dois segundos e meio de sino, incluindo a ressonância e o eco finais.
      const bellOutput = context.createGain()
      bellOutput.gain.setValueAtTime(1, bellStart)
      bellOutput.gain.setValueAtTime(1, bellEnd - .35)
      bellOutput.gain.linearRampToValueAtTime(0, bellEnd)
      bellOutput.connect(output)
      const echo = context.createDelay(.5)
      const echoLevel = context.createGain()
      const feedback = context.createGain()
      echo.delayTime.value = .075
      echoLevel.gain.value = .12
      feedback.gain.value = .18
      bellBus.connect(bellOutput)
      bellBus.connect(echo)
      echo.connect(echoLevel); echoLevel.connect(bellOutput)
      echo.connect(feedback); feedback.connect(echo)
      // Modos medidos no MP3 de referência enviado pelo usuário.
      // A ressonância de 3,5 kHz predomina; os modos altos decaem primeiro.
      for (const offset of Array.from({ length: 17 }, (_, i) => .06 + i * .10)) {
        // O sino entra logo após o ataque do único lançamento (0,18 s).
        const hit = start + .12 + offset
        for (const [frequency, volume, decay] of [[1203, .075, 4.1], [3496.5, .145, 3.7], [5620, .05, .7], [5628, .035, .65], [8110, .025, .28]]) {
          const oscillator = context.createOscillator()
          const envelope = context.createGain()
          oscillator.type = 'sine'
          oscillator.frequency.setValueAtTime(frequency, hit)
          envelope.gain.setValueAtTime(0, hit)
          envelope.gain.linearRampToValueAtTime(volume, hit + .003)
          envelope.gain.exponentialRampToValueAtTime(volume * .4, hit + decay * .3)
          envelope.gain.exponentialRampToValueAtTime(.0001, hit + decay)
          oscillator.connect(envelope); envelope.connect(bellBus)
          oscillator.start(hit); oscillator.stop(hit + decay + .02)
        }
      }
      // A cauda sonora termina um pouco antes dos últimos confetes.
      const fallingSoundEnd = CONFETTI_END - .4
      for (let i = 0; i < 24; i++) {
        const progress = i / 23
        const when = start + CONFETTI_LANDING_SOUND_START + progress * (fallingSoundEnd - CONFETTI_LANDING_SOUND_START - .1)
        const duration = .045 + Math.random() * .045
        const source = context.createBufferSource()
        const filter = context.createBiquadFilter()
        const envelope = context.createGain()
        source.buffer = noise
        filter.type = 'bandpass'
        filter.frequency.value = 1800 + Math.random() * 2200
        filter.Q.value = .75
        const volume = (.045 + Math.random() * .065) * (1 - progress * .8)
        envelope.gain.setValueAtTime(0, when)
        envelope.gain.linearRampToValueAtTime(volume, when + duration * .25)
        envelope.gain.exponentialRampToValueAtTime(.0001, when + duration)
        source.connect(filter); filter.connect(envelope); envelope.connect(output)
        source.start(when, Math.random() * .35)
        source.stop(when + duration + .01)
      }
      playing.timer = setTimeout(() => { if (active.current === playing) stop() }, (Math.max(fallingSoundEnd, bellEnd - start) + .15) * 1000)
    } catch {
      // Falha/bloqueio de áudio não interfere na alteração do negócio.
      if (active.current === playing) stop()
    }
  }
}
